import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import {
  deadlineDisplay,
  formatShortDate,
  isVerifiedToday,
  programTypeLabel,
  programTypeTone,
  toneClasses,
} from "@/lib/grants-utils";

export type GrantCardData = {
  id: string;
  name: string;
  scheme: string | null;
  country: string | null;
  regional_focus: string | null;
  host_organization: string | null;
  program_type: string | null;
  amount: string | null;
  closing_date: string | null;
  is_rolling: boolean | null;
  status: string | null;
  updated_at: string | null;
  eligibility: string[] | null;
  application_url?: string | null;
  emails?: string[] | null;
};

function DeadlinePill({ g }: { g: GrantCardData }) {
  const d = deadlineDisplay(g);
  const cls =
    d.variant === "urgent"
      ? "bg-[color:var(--color-peach)] text-ink"
      : d.variant === "rolling"
      ? "bg-[color:var(--color-accent-mint)] text-ink"
      : d.variant === "expired"
      ? "bg-[color:var(--color-peach)] text-ink"
      : "bg-surface-sunken text-ink";
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11.5px] font-semibold ${cls}`}
    >
      {d.label}
    </span>
  );
}

export function GrantFlipCard({ grant, isLoggedIn }: { grant: GrantCardData; isLoggedIn: boolean }) {
  const [flipped, setFlipped] = useState(false); // for mobile tap
  const navigate = useNavigate();
  const type = programTypeLabel(grant.program_type);
  const tone = programTypeTone(grant.program_type);
  const verifiedToday = isVerifiedToday(grant.updated_at);
  const hostLine = [grant.host_organization ?? grant.scheme, grant.country ?? grant.regional_focus]
    .filter(Boolean)
    .join(" · ");
  const eligibilityTop = (grant.eligibility ?? []).slice(0, 3);
  const isExpired = grant.status === "expired";

  const goToDetail = () => {
    navigate({ to: "/grants/$id", params: { id: grant.id } });
  };

  const handleCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    // Let real interactive elements handle their own click
    if (target.closest("[data-card-action]")) return;
    const isTouch = typeof window !== "undefined" && window.matchMedia("(hover: none)").matches;
    if (isTouch && !flipped) {
      // First tap on touch: flip to reveal back
      e.preventDefault();
      setFlipped(true);
      return;
    }
    goToDetail();
  };

  return (
    <div
      className="group [perspective:1200px] h-[300px] cursor-pointer"
      onClick={handleCardClick}
      role="link"
      aria-label={`View ${grant.name}`}
    >
      <div
        className={`relative h-full w-full transition-transform duration-[550ms] [transform-style:preserve-3d] group-hover:[transform:rotateY(180deg)] ${
          flipped ? "[transform:rotateY(180deg)]" : ""
        }`}
        style={{ transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)" }}
      >
        {/* FRONT */}
        <div className="absolute inset-0 flex flex-col rounded-2xl border border-line bg-surface p-5 [backface-visibility:hidden]">
          <div className="mb-3 flex items-center justify-between gap-2">
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wider ${toneClasses(
                tone,
              )}`}
            >
              {type}
            </span>
            {isExpired ? (
              <span className="inline-flex items-center rounded-full bg-[color:var(--color-peach)] px-2 py-0.5 text-[10.5px] font-bold uppercase text-ink">
                Deadline expired
              </span>
            ) : verifiedToday ? (
              <span className="inline-flex items-center rounded-full bg-[color:var(--color-accent-mint)] px-2 py-0.5 text-[10.5px] font-bold uppercase text-ink">
                ✓ Verified today
              </span>
            ) : grant.updated_at ? (
              <span className="text-[10.5px] font-medium uppercase tracking-wider text-ink-tertiary">
                Verified {formatShortDate(grant.updated_at)}
              </span>
            ) : null}
          </div>

          <div
            className="font-space-grotesk text-[17px] font-bold leading-snug text-ink"
            style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
          >
            {grant.name}
          </div>
          {hostLine && (
            <p className="mt-1 text-[13px] text-muted-ink">{hostLine}</p>
          )}

          <div className="mt-auto flex items-end justify-between gap-3 pt-4">
            <div>
              <div
                className="text-[22px] font-bold leading-none text-ink"
                style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
              >
                {grant.amount ?? "-"}
              </div>
              <div className="mt-2">
                <DeadlinePill g={grant} />
              </div>
            </div>
          </div>
          {eligibilityTop.length > 0 && (
            <div className="mt-3 text-[11px] text-ink-tertiary">hover for eligibility ↻</div>
          )}
        </div>

        {/* BACK */}
        <div
          className="absolute inset-0 flex flex-col rounded-2xl bg-[color:var(--color-peach)] p-5 text-ink [backface-visibility:hidden]"
          style={{ transform: "rotateY(180deg)" }}
        >
          <div className="text-[10.5px] font-bold uppercase tracking-wider text-ink">
            Eligibility
          </div>
          <ul className="mt-3 flex flex-col gap-2 text-[13.5px] leading-snug text-ink">
            {eligibilityTop.length === 0 ? (
              <li className="text-ink/70">Eligibility: on official site ↗</li>
            ) : (
              eligibilityTop.map((item, i) => (
                <li key={i} className="flex gap-2">
                  <span
                    className="mt-0.5 inline-block h-4 w-4 flex-none rounded-full text-center text-[10px] font-bold leading-4 text-ink"
                    style={{ background: "var(--color-accent-mint)" }}
                  >
                    ✓
                  </span>
                  <span className="line-clamp-2">{item}</span>
                </li>
              ))
            )}
          </ul>

          <div
            className="mt-auto pt-3"
            style={{ borderTop: "1px solid rgba(0,0,0,0.15)" }}
          >
            {isLoggedIn && grant.application_url ? (
              <div className="flex flex-col gap-2">
                <div className="text-[11.5px] text-ink/75">
                  Contact:{" "}
                  {grant.emails && grant.emails.length > 0 ? (
                    <a
                      data-card-action
                      href={`mailto:${grant.emails[0]}`}
                      onClick={(e) => e.stopPropagation()}
                      className="font-semibold text-ink underline"
                    >
                      {grant.emails[0]}
                    </a>
                  ) : (
                    <span>-</span>
                  )}
                </div>
                <div className="flex items-center justify-between gap-2">
                  <a
                    data-card-action
                    href={grant.application_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center justify-center rounded-full bg-ink px-4 py-2 text-[13px] font-bold text-[color:var(--color-bg)]"
                  >
                    Open application →
                  </a>
                  <span className="text-[11.5px] font-semibold text-ink/75 underline">
                    View details →
                  </span>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <div className="text-[11.5px] text-ink/75">
                  Contact:{" "}
                  <span className="text-ink" style={{ filter: "blur(4px)" }}>hidden@grantorg.io</span>{" "}
                  · Apply:{" "}
                  <span className="text-ink" style={{ filter: "blur(4px)" }}>grantorg.io/apply</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <Link
                    data-card-action
                    to="/login"
                    search={{ next: `/grants/${grant.id}` }}
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center justify-center rounded-full bg-ink px-4 py-2 text-[13px] font-bold text-[color:var(--color-bg)]"
                  >
                    🔒 Free account to view →
                  </Link>
                  <span className="text-[11.5px] font-semibold text-ink/75 underline">
                    View details →
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
